import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { UserAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProfileContext from '../context/ProfileContext';
import moment from 'moment';
import axios from 'axios';
import { useUserContext } from '../context/UserContext';
import { useStepContext } from '../context/StepContext';
import { Button } from 'react-bootstrap';
import SuccessIcon from '../assets/success.png';

const DownloadPage = () => {
  const { userData, handleUserChange } = useUserContext();
  const navigate = useNavigate();
  const { user } = UserAuth();
  const { profile, setProfile } = useContext(ProfileContext);
  const { setIsCompleted, setCurrentStep } = useStepContext();

  const [timerSeconds, setTimerSeconds] = useState(0);
  const [interviewStatus, setInterviewStatus] = useState(null);
  const [interviewDateTime, setInterviewDateTime] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [candidateList, setCandidateList] = useState([]);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(0);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const hasInitialized = useRef(false);
  const lastUid = useRef(null);

  const isInterviewWithin48Hours = (interview_date, interview_time) => {
    if (!interview_date || !interview_time) return { status: 'expired', remainingSeconds: 0, interviewDateTimeIST: null };

    const dateStr = interview_date.split('T')[0];
    const timeStr = interview_time.split('.')[0];

    const interviewDateTimeIST = moment(`${dateStr} ${timeStr}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"]);
    const interviewEndTime = interviewDateTimeIST.clone().add(48, 'hours');
    const now = moment();
    const remainingSeconds = interviewEndTime.diff(now, 'seconds');

    if (now.isBefore(interviewDateTimeIST)) {
      return { status: 'active', remainingSeconds: remainingSeconds, interviewDateTimeIST: interviewDateTimeIST.toDate() };
    } else if (now.isBetween(interviewDateTimeIST, interviewEndTime) && remainingSeconds > 0) {
      return { status: 'active', remainingSeconds: remainingSeconds > 0 ? remainingSeconds : 0, interviewDateTimeIST: interviewDateTimeIST.toDate() };
    } else {
      return { status: 'expired', remainingSeconds: 0, interviewDateTimeIST: interviewDateTimeIST.toDate() };
    }
  };

  const handleCandidateSelection = useCallback((index, candidates, isInitialLoad = false) => {
    if (!candidates || candidates.length === 0) return;

    if (!isInitialLoad) {
      setSelectionLoading(true);
    }

    const candidate = candidates[index];
    setSelectedCandidateIndex(index);

    handleUserChange("getJobtitle", candidate?.requisitionDetails?.jobTitle || "");
    handleUserChange("getReqIdValue", candidate?.requisition_id || "");

    if (candidate.source === 'previous') {
      const rawDate = candidate.interview_date || candidate.test_date || candidate.created_at || candidate.submitted_at;
      const rawTime = candidate.interview_time || "00:00:00";

      let interviewDateTimeIST;

      if (rawDate && String(rawDate).includes('T')) {
        interviewDateTimeIST = moment(rawDate).toDate();
      } else if (rawDate) {
        const datePart = String(rawDate).split(' ')[0];
        const timePart = String(rawTime).split('.')[0];
        interviewDateTimeIST = moment(`${datePart} ${timePart}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD", "DD-MM-YYYY"]).toDate();
      } else {
        interviewDateTimeIST = new Date();
      }

      setInterviewStatus('completed');
      setTimerSeconds(0);
      setInterviewDateTime(interviewDateTimeIST);
    } else {
      const { status, remainingSeconds, interviewDateTimeIST } = isInterviewWithin48Hours(
        candidate?.interview_date,
        candidate?.interview_time
      );
      setInterviewStatus(status);
      setTimerSeconds(remainingSeconds);
      setInterviewDateTime(interviewDateTimeIST);
    }

    setTimeout(() => {
      setSelectionLoading(false);
    }, 600);
  }, [handleUserChange]);

  const getScheduledata = useCallback(async () => {
    const candidate_Email = user?.email;
    if (!candidate_Email) return;

    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      const headers = { Authorization: `Bearer ${refreshedToken}` };

      const scheduledReq = axios.post(`${process.env.REACT_APP_CANDIDATE}/getCandidateDetailsRegistration`, { email: candidate_Email }, { headers }).catch(() => null);
      const previousTestsReq = axios.post(`${process.env.REACT_APP_LAMBDA_WEB}/get-previous-tests`, { email: candidate_Email }, { headers }).catch(() => null);

      const [scheduledRes, previousTestsRes] = await Promise.all([scheduledReq, previousTestsReq]);

      let combinedCandidates = [];

      if (scheduledRes?.data?.statusCode === 200) {
        combinedCandidates = (scheduledRes?.data?.candidate || []).map(cand => ({ ...cand, source: 'scheduled' }));
      }

      if (previousTestsRes?.data && Array.isArray(previousTestsRes.data)) {
        const previousTests = previousTestsRes.data.map(cand => ({ ...cand, source: 'previous' }));
        combinedCandidates = [...combinedCandidates, ...previousTests];
      }

      const uniqueItems = new Map();
      combinedCandidates.forEach(cand => {
        if (cand.requisition_id && !uniqueItems.has(cand.requisition_id)) {
          uniqueItems.set(cand.requisition_id, cand);
        }
      });

      const uniqueCandidatesList = Array.from(uniqueItems.values());

      setCandidateList(uniqueCandidatesList);
      if (uniqueCandidatesList.length > 0) {
        const currentReqId = userData?.getReqIdValue || profile?.userfetchedData?.requisition_id;

        let initialIndex = 0;
        if (currentReqId) {
          const matchingIndex = uniqueCandidatesList.findIndex(cand =>
            String(cand.requisition_id) === String(currentReqId) && cand.source === 'scheduled'
          );
          if (matchingIndex !== -1) {
            initialIndex = matchingIndex;
          } else {
            const anyScheduledIndex = uniqueCandidatesList.findIndex(cand => cand.source === 'scheduled');
            if (anyScheduledIndex !== -1) initialIndex = anyScheduledIndex;
          }
        }

        handleCandidateSelection(initialIndex, uniqueCandidatesList, true);
      }
    } catch (error) {
      console.error('Error fetching schedule data:', error);
    }
  }, [user, handleCandidateSelection]);

  const fetchUserProfile = useCallback(async () => {
    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };
      const response = await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`, {
        operation: 'fetchuser',
        email: user?.email,
      }, { headers });

      const getuserData = response.data.data[0];
      const hasPhoto = !!(getuserData?.user_photo?.trim());
      const hasResume = !!(getuserData?.resume_url?.trim());
      const isNumberVerified = getuserData?.is_number_verified === 1;
      const verified = hasPhoto && hasResume && isNumberVerified;
      const empAdded = getuserData?.is_employement_added === 1;
      const eduAdded = getuserData?.is_edu_added === 1;
      const headlineAdded = getuserData?.resume_headline_added === 1;

      setProfile((prev) => ({
        ...prev,
        prefered_lang: getuserData?.prefered_lang,
        is_profile_verified: getuserData?.resume_headline_added,
        userfetchedData: getuserData
      }));

      if (verified && empAdded && eduAdded && headlineAdded) {
        setIsCompleted(true);
        setCurrentStep(2);
      } else {
        setIsCompleted(false);
        setCurrentStep(1);
      }
    } catch (error) {
      console.error(error);
    }
  }, [user, setProfile, setIsCompleted, setCurrentStep]);

  useEffect(() => {
    if (!user?.uid) return;
    if (hasInitialized.current && lastUid.current === user.uid) return;

    hasInitialized.current = true;
    lastUid.current = user.uid;

    const init = async () => {
      setPageLoading(true);
      try {
        await Promise.all([fetchUserProfile(), getScheduledata()]);
      } catch (error) {
        console.error("Initial load error:", error);
      } finally {
        setPageLoading(false);
      }
    };
    init();
  }, [user?.uid, fetchUserProfile, getScheduledata]);

  const recordAppDownload = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_APIURL_NMVE}/v3.0/otp/recordAppDownload`, {
        email: user?.email
      });
    } catch (error) {
      console.error('Error recording download:', error);
    }
  };

  // Custom Countdown Timer
  const CountdownTimer = ({ totalSeconds, onTimerEnd, compact = false }) => {
    const [secondsLeft, setSecondsLeft] = useState(totalSeconds);
    const timerEndedRef = useRef(false);
    const onTimerEndRef = useRef(onTimerEnd);
    onTimerEndRef.current = onTimerEnd;

    useEffect(() => {
      setSecondsLeft(totalSeconds);
      timerEndedRef.current = false;
    }, [totalSeconds]);

    useEffect(() => {
      if (totalSeconds <= 0) {
        if (!timerEndedRef.current) {
          timerEndedRef.current = true;
          onTimerEndRef.current?.();
        }
        return;
      }

      // Fix: depending on [secondsLeft] recreated the interval every tick and could fire onTimerEnd multiple times.
      const interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            if (!timerEndedRef.current) {
              timerEndedRef.current = true;
              onTimerEndRef.current?.();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }, [totalSeconds]);

    const splitTime = (secs) => ({
      days: Math.floor(secs / 86400),
      hours: Math.floor((secs % 86400) / 3600),
      mins: Math.floor((secs % 3600) / 60),
      secs: secs % 60,
    });

    const pad2 = (n) => String(n).padStart(2, '0');
    const parts = splitTime(secondsLeft);
    const isLongDuration = secondsLeft >= 86400;
    const toneClass = secondsLeft === 0 ? 'text-success' : 'text-warning';

    const unitBoxStyle = {
      background: 'rgba(255, 255, 255, 0.14)',
      border: '1px solid rgba(255, 255, 255, 0.22)',
      minWidth: '72px',
      backdropFilter: 'blur(4px)',
    };

    const renderUnit = (value, label) => (
      <div
        key={label}
        className="d-flex flex-column align-items-center justify-content-center px-3 py-2 rounded-3 shadow-sm"
        style={unitBoxStyle}
      >
        <span className="fs-2 fs-md-3 fw-bold lh-1 text-white" style={{ letterSpacing: '1px', fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </span>
        <span className="fs-12 text-white text-opacity-75 text-uppercase mt-1" style={{ letterSpacing: '0.6px' }}>
          {label}
        </span>
      </div>
    );

    const renderSeparator = (key) => (
      <span key={key} className="fs-4 fw-light text-white text-opacity-50 align-self-center" aria-hidden="true">:</span>
    );

    const formatLongDurationText = () => {
      const segments = [];
      if (parts.days > 0) segments.push(`${parts.days} ${parts.days === 1 ? 'day' : 'days'}`);
      segments.push(`${parts.hours} ${parts.hours === 1 ? 'hour' : 'hours'}`);
      segments.push(`${parts.mins} ${parts.mins === 1 ? 'min' : 'mins'}`);
      return segments.join(' ');
    };

    if (compact) {
      return (
        <span className={`fw-bold ${secondsLeft === 0 ? 'text-deep-purple' : ''}`}>
          {isLongDuration
            ? formatLongDurationText()
            : `${pad2(parts.hours)}:${pad2(parts.mins)}:${pad2(parts.secs)}`}
        </span>
      );
    }

    if (isLongDuration) {
      return (
        <div className={`d-flex align-items-center justify-content-center flex-wrap gap-2 gap-sm-3 mt-2 ${toneClass}`}>
          {renderUnit(parts.days, parts.days === 1 ? 'day' : 'days')}
          {renderSeparator('sep-days')}
          {renderUnit(pad2(parts.hours), parts.hours === 1 ? 'hour' : 'hours')}
          {renderSeparator('sep-hours')}
          {renderUnit(pad2(parts.mins), 'min')}
        </div>
      );
    }

    return (
      <div className={`d-flex align-items-center justify-content-center flex-wrap gap-2 gap-sm-3 mt-2 ${toneClass}`}>
        {renderUnit(pad2(parts.hours), 'hrs')}
        {renderSeparator('sep-hrs')}
        {renderUnit(pad2(parts.mins), 'min')}
        {renderSeparator('sep-mins')}
        {renderUnit(pad2(parts.secs), 'sec')}
      </div>
    );
  };

  if (pageLoading) {
    return (
      <div className="profile-page py-3">
        <div className="container">
          <div className="info-wrapper">
            <div className="alert alert-success d-flex align-items-center gap-2 rounded-3 mb-3 py-2" role="alert">
              <div className="spinner-border spinner-border-sm text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span>Preparing, please wait...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page py-3">
      <div className="container">
        {/* Download Section */}
        <div className="download-app-sec">
          <div className="banner-section position-relative rounded-4 text-center overflow-hidden mb-3 mb-xxl-4 px-3 py-4">
            <div className="content-wrapper mx-auto" style={{ maxWidth: "700px" }}>
              <div className="img-wrapper mb-3">
                <img src={SuccessIcon} alt='Success' className="img-fluid" style={{ width: '50px' }} />
              </div>
              {candidateList.length > 0 ? (
                <div className="wrapper mb-2">
                  <select
                    className="form-select form-select-sm d-inline-flex w-auto small text-truncate bg-theme7 bg-opacity-25 border border-theme7 border-opacity-50 rounded-pill text-theme7 fw-medium ps-3 py-1"
                    value={selectedCandidateIndex}
                    onChange={(e) =>
                      handleCandidateSelection(Number(e.target.value), candidateList)
                    }
                    style={{
                      cursor: "pointer",
                      boxShadow: "none",
                      maxWidth: "100%",
                      paddingRight: "2rem",
                      backgroundPosition: "right 0.75rem center",
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")",
                    }}>
                    {candidateList.map((cand, idx) => (
                      <option
                        key={cand.id || idx}
                        value={idx}
                        className="text-dark bg-white fw-normal">
                        {cand.requisitionDetails?.jobTitle || cand.user_name}{" "}
                        {cand.requisition_id
                          ? `- VEREQ${cand.requisition_id}`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="d-inline-flex small bg-theme7 bg-opacity-25 border border-theme7 border-opacity-50 rounded-pill text-theme7 mb-3 px-3 py-1">
                  {userData?.getJobtitle ? userData?.getJobtitle : "---"}
                </div>
              )}
              <div className="wrapper mb-2 pb-1">
                <div className="fs-3 fw-bold lh-sm text-theme7 mb-2 mb-xxl-3">You're All Set</div>
                <div className="fs-14 text-white text-opacity-75">
                  Your profile is verified and complete. Download the interview
                  app and start your AI interview.
                </div>
              </div>
              <div className="position-relative info-card text-center mt-0">
                {selectionLoading ? (
                  <div className="d-flex align-items-center justify-content-center py-4">
                    <div className="spinner-border spinner-border-sm text-theme7" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-2 text-white text-opacity-75 small">Switching requisition...</span>
                  </div>
                ) : (
                  <>
                    {interviewStatus === "active" && (
                      <>
                        <div className="fs-16 fw-semibold text-white mb-1">
                          <i className="bi bi-stopwatch me-1"></i>Time remaining to complete the interview.
                        </div>
                        <div className="countdown-wrapper">
                          <CountdownTimer
                            totalSeconds={timerSeconds}
                            onTimerEnd={() => setInterviewStatus("expired")}
                          />
                        </div>
                      </>
                    )}
                    {
                      interviewStatus === "upcoming" && (
                        <div className="countdown-wrapper d-flex align-items-center justify-content-center flex-wrap gap-2">
                          <span className="fs-6 fw-semibold text-white text-nowrap">
                            <i className="bi bi-calendar4-week me-1"></i> Your interview has been scheduled for
                          </span>
                          <span className="badge bg-warning text-dark fs-12 rounded-3 text-nowrap px-3 py-2">
                            {interviewDateTime &&
                              moment(interviewDateTime).format(
                                "DD MMM YYYY, hh:mm A",
                              )}
                          </span>
                        </div>
                      )}
                    {interviewStatus === "expired" && (
                      <div className="fs-16 fw-semibold text-warning">
                        <i className="bi bi-exclamation-octagon me-1"></i> Interview Window Expired.
                      </div>
                    )}
                    {interviewStatus === "completed" && (
                      <div className="countdown-wrapper d-flex align-items-center justify-content-center flex-wrap gap-2">
                        <span className="fs-6 fw-semibold text-white text-nowrap">
                          <i className="bi bi-check-circle-fill fs-16 me-1"></i> Your interview has been completed on
                        </span>
                        <span className="badge bg-success text-white fs-12 rounded-3 text-nowrap px-3 py-2">
                          {interviewDateTime &&
                            moment(interviewDateTime).format(
                              "DD MMM YYYY, hh:mm A",
                            )}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div >

          <div className="wrapper mx-auto" style={{ maxWidth: "900px" }}>
            <div className={(interviewStatus !== "expired" && interviewStatus !== "completed") ? "layout-wrapper" : "w-100"}>
              {(interviewStatus !== "expired" && interviewStatus !== "completed") && (
                <div className="left-section">
                  <div className="info-card bg-white border border-2 border-primary rounded-4 p-3 p-sm-4">

                    <div className="fs-4 fw-bold text-dark mb-2">
                      Download Your Interview App
                    </div>
                    <p className="description text-muted mb-3">
                      Download and install our official interview tool on your
                      computer or laptop, then log in using your registered
                      mobile number to begin the online test.
                    </p>
                    {profile?.userfetchedData?.contact_no && (
                      <div className="d-flex align-items-center gap-2 mb-4 px-3 py-2 rounded-3" style={{ backgroundColor: '#f3f0ff', border: '1px solid #e9e3ff' }}>
                        <i className="bi bi-phone-fill" style={{ color: '#7c3aed' }}></i>
                        <span className="small" style={{ color: '#4b5563' }}>
                          Registered number: <span className="fw-semibold text-dark">{profile.userfetchedData.contact_no}</span>
                        </span>
                      </div>
                    )}
                    <div className="btn-wrapper text-center">
                      <a
                        href="https://electronbuild.s3.ap-south-1.amazonaws.com/NMinterview.exe"
                        className="text-decoration-none"
                        download="NM-Interview App"
                        title="Download NM-Interview App"
                        onClick={recordAppDownload}>
                        <Button
                          variant="primary"
                          className="btn-custom theme-gradient-bg d-flex align-items-center justify-content-center gap-2 w-100">
                          <i className="bi bi-cloud-download"></i> Download
                          App for Windows
                        </Button>
                      </a>
                      <div className="tagline fs-12 d-flex flex-wrap align-items-center justify-content-center gap-1 text-muted mt-2">
                        <span>Require Windows 7 or above</span>
                        <i className="bi bi-dot"></i>
                        <span>Free to Install</span>
                        <i className="bi bi-dot"></i>
                        <span className="text-theme6">
                          <i className="bi bi-shield-fill-check"></i> Secured
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`right-section ${(interviewStatus === "expired" || interviewStatus === "completed") ? "w-100 d-flex flex-column flex-lg-row justify-content-center align-items-start gap-4 mx-auto" : ""}`}>
                <div className={`info-card bg-white border h-100 rounded-4 p-3 p-sm-4 ${(interviewStatus === "expired" || interviewStatus === "completed") ? "mb-0" : "mb-0"}`}
                  style={
                    (interviewStatus === "expired" || interviewStatus === "completed")
                      ? { width: "100%", maxWidth: "450px" }
                      : {}
                  }>
                  <div className="fs-16 fw-semibold text-primary mb-3">
                    What to expect in the interview
                  </div>
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-start gap-2 small text-secondary">
                      <i className="bi bi-stopwatch-fill text-theme7 fs-6"></i>
                      <div>
                        Takes approximately{" "}
                        <span className="fw-bold text-black text-opacity-75">
                          60 minutes
                        </span>{" "}
                        to complete.
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 small text-secondary">
                      <i className="bi bi-file-earmark-music-fill lh-sm text-theme7 fs-6"></i>
                      <div>
                        Answer via{" "}
                        <span className="fw-bold text-black text-opacity-75">
                          audio and text
                        </span>
                        .
                      </div>
                    </div>
                    <div className="d-flex align-items-start gap-2 small text-secondary">
                      <i className="bi bi-house-door-fill text-theme7 fs-6"></i>
                      <div>
                        Complete from anywhere —{" "}
                        <span className="fw-bold text-black text-opacity-75">
                          no human interviewer
                        </span>
                        .
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div >
      </div >
    </div >
  );
};

export default DownloadPage;
